import type { CallPrepVisualStatus } from "../lib/call-prep-status.ts";
import type { EvArtifactFreshnessState } from "../types/index.ts";

export interface CallPrepStructuredData {
  summary: string | null;
  objective: string | null;
  whyNow: string | null;
  relationshipSnapshot: string | null;
  priorCallHighlights: string[];
  commercialState: {
    softCap: number | string | null;
    hardCap: number | string | null;
    fdv: number | string | null;
    saleTiming: string | null;
    tgeTiming: string | null;
  } | null;
  teamInvestorContext: string[];
  talkingPoints: string[];
  questionsToAsk: string[];
  risksObjections: string[];
  missingInputs: string[];
  desiredNextStep: string | null;
  sourceSummary?: string[];
  qualityFlags?: string[];
  openQuestions?: string[];
  generatedByModel?: string | null;
  generatedAt?: string | null;
  freshnessState?: EvArtifactFreshnessState | null;
}

const commercialAmountFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatCommercialAmount(value: number | string | null | undefined): string | null {
  if (value == null) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? commercialAmountFormatter.format(value) : null;
  }

  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return null;

  const numericCandidate = normalized.replaceAll(",", "").replace(/^\$/, "");
  if (!/^-?\d+(?:\.\d+)?$/.test(numericCandidate)) {
    return normalized;
  }

  const parsed = Number(numericCandidate);
  return Number.isFinite(parsed) ? commercialAmountFormatter.format(parsed) : normalized;
}

function formatCommercialFact(
  label: string,
  value: number | string | null | undefined
): string | null {
  const formattedValue = formatCommercialAmount(value);
  return formattedValue ? `${label}: ${formattedValue}` : null;
}

function toDisplayList(items: string[] | null | undefined): string[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function renderList(items: string[] | null | undefined, emptyLabel: string) {
  const displayItems = toDisplayList(items);

  if (displayItems.length === 0) {
    return <div className="call-prep-structured__empty">{emptyLabel}</div>;
  }

  const itemCounts = new Map<string, number>();

  return (
    <ul className="call-prep-structured__list">
      {displayItems.map((item) => {
        const nextCount = itemCounts.get(item) ?? 0;
        itemCounts.set(item, nextCount + 1);
        const key = nextCount === 0 ? item : `${item}-${nextCount}`;
        return <li key={key}>{item}</li>;
      })}
    </ul>
  );
}

function renderValue(value: string | null | undefined, emptyLabel: string) {
  return value ? (
    <div className="call-prep-structured__text">{value}</div>
  ) : (
    <div className="call-prep-structured__empty">{emptyLabel}</div>
  );
}

function getCallPrepStatusLabel(status: CallPrepVisualStatus): string {
  switch (status) {
    case "fresh":
      return "Prepared brief";
    case "stale":
      return "Needs update";
    case "generating":
      return "Preparing";
    case "needs_input":
      return "Not enough information";
    case "failed":
      return "Failed";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export function buildCallPrepClipboardText(
  projectLabel: string,
  status: CallPrepVisualStatus,
  data: CallPrepStructuredData
): string {
  const priorCallHighlights = toDisplayList(data.priorCallHighlights);
  const talkingPoints = toDisplayList(data.talkingPoints);
  const questionsToAsk = toDisplayList(data.questionsToAsk);
  const risksObjections = toDisplayList(data.risksObjections);
  const missingInputs = toDisplayList(data.missingInputs);
  const lines = [
    projectLabel,
    `Status: ${getCallPrepStatusLabel(status)}`,
    data.summary ? `Summary: ${data.summary}` : null,
    data.objective ? `Objective: ${data.objective}` : null,
    data.whyNow ? `Why now: ${data.whyNow}` : null,
    data.relationshipSnapshot ? `Relationship snapshot: ${data.relationshipSnapshot}` : null,
    priorCallHighlights.length > 0
      ? `Prior call highlights: ${priorCallHighlights.join(" | ")}`
      : null,
    talkingPoints.length > 0 ? `Talking points: ${talkingPoints.join(" | ")}` : null,
    questionsToAsk.length > 0 ? `Questions: ${questionsToAsk.join(" | ")}` : null,
    risksObjections.length > 0 ? `Risks: ${risksObjections.join(" | ")}` : null,
    missingInputs.length > 0 ? `Missing inputs: ${missingInputs.join(" | ")}` : null,
    data.desiredNextStep ? `Desired next step: ${data.desiredNextStep}` : null,
  ];

  return lines.filter((line): line is string => Boolean(line)).join("\n");
}

export function CallPrepStatusBadge({ status }: { status: CallPrepVisualStatus }) {
  return (
    <span className={`call-prep-status-badge call-prep-status-badge--${status}`}>
      {getCallPrepStatusLabel(status)}
    </span>
  );
}

export function CallPrepStructuredView({
  data,
  status,
  showProvenance = false,
}: {
  data: CallPrepStructuredData;
  status: CallPrepVisualStatus;
  showProvenance?: boolean;
}) {
  const commercialFacts = [
    formatCommercialFact("Soft cap", data.commercialState?.softCap),
    formatCommercialFact("Hard cap", data.commercialState?.hardCap),
    formatCommercialFact("FDV", data.commercialState?.fdv),
    data.commercialState?.saleTiming ? `Sale timing: ${data.commercialState.saleTiming}` : null,
    data.commercialState?.tgeTiming ? `TGE timing: ${data.commercialState.tgeTiming}` : null,
  ].filter((item): item is string => Boolean(item));
  const generatedAt = formatDateTime(data.generatedAt);

  return (
    <div className="call-prep-structured">
      {data.summary ? <div className="call-prep-structured__summary">{data.summary}</div> : null}
      <div className="call-prep-structured__grid">
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Objective</div>
          {renderValue(data.objective, "Objective not available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Why Now</div>
          {renderValue(data.whyNow, "Why-now context not available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Relationship Snapshot</div>
          {renderValue(data.relationshipSnapshot, "Relationship snapshot not available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Desired Next Step</div>
          {renderValue(data.desiredNextStep, "Desired next step not available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Prior Call Highlights</div>
          {renderList(data.priorCallHighlights, "No prior call highlights available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Commercial State</div>
          {renderList(commercialFacts, "Commercial state is incomplete.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Team / Investor Context</div>
          {renderList(data.teamInvestorContext, "Team and investor context is limited.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Talking Points</div>
          {renderList(data.talkingPoints, "No talking points available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Questions To Ask</div>
          {renderList(data.questionsToAsk, "No open questions available.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">Risks / Objections</div>
          {renderList(data.risksObjections, "No explicit call risks recorded.")}
        </div>
        <div className="call-prep-structured__section">
          <div className="call-prep-structured__label">
            {status === "needs_input" ? "Not Enough Information" : "Missing Inputs"}
          </div>
          {status === "needs_input" ? (
            <div className="call-prep-structured__text">
              There is not enough relationship or commercial context yet to generate a credible call
              brief.
            </div>
          ) : null}
          {renderList(
            data.missingInputs,
            status === "needs_input"
              ? "Add more relationship or commercial context to generate a credible brief."
              : "No missing inputs flagged."
          )}
        </div>
      </div>

      {showProvenance ? (
        <div className="call-prep-structured__footer">
          <div className="call-prep-structured__footer-row">
            <CallPrepStatusBadge status={status} />
            {data.generatedByModel ? <span>Generated by {data.generatedByModel}</span> : null}
            {generatedAt ? <span>{generatedAt}</span> : null}
            {data.freshnessState ? <span>{data.freshnessState}</span> : null}
          </div>
          {data.sourceSummary && data.sourceSummary.length > 0 ? (
            <div className="call-prep-structured__provenance">
              <div className="call-prep-structured__label">Generated From</div>
              {renderList(data.sourceSummary, "Source provenance unavailable.")}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
